/** @jsxImportSource @emotion/react */
import * as React from "react";
import { useSelector } from "react-redux";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import * as Inputs from "../common/hook-form/index";
import { SpinnerBlock } from "../common/Spinner";
import { Organization } from "./model/OrganizationSuccessResponse";
import { GoBackLinkButton } from "../../components/GoBackLinkButton";
import { routes } from "../../appRoutingHelpers";
import { useGetOrganizationTerms } from "../../service/api";
import * as authSelectors from "../auth/authSelectors";
import { PageWrapper } from "../../components/page-skeleton/PageWrapper";
import { CardContainer } from "../../components/page-skeleton/CardContainer";
import { PageHeader } from "../../components/page-skeleton/PageHeader";
import { CardBody } from "../../components/page-skeleton/CardBody";
import { TextInput } from "../../components/forms/inputs/TextInput";
import { css } from "@emotion/react";
import { CheckboxInput } from "../../components/forms/inputs/CheckboxInput";
import { StyledForm } from "../../components/StyledForm";
import { StickyBottomBar } from "../../components/StickyBottomBar";
import { stickyBottomBarBodyStyle } from "../shop/app-home/generic-styles";
import { ButtonAction } from "../../components/NewButtons";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { SelectInput } from "../../components/forms/inputs/SelectInput";
import {
  useGetOrganization,
  useUpdateOrganization,
  useUploadOrganizationReceiptImage,
} from "./organization-service/organization-api";
import { toastr } from "react-redux-toastr";
import { BaseUploadPreview } from "../../components/BaseUploadPreview";
import { StyledText } from "../../assets/typography/typography";
import { colors } from "../../components/colors";
import { http } from "../../service/http";
import { getErrorMessage, useHandleFetchError } from "../../error-handler";
import { AsyncSelectInput } from "../../components/forms/inputs/AsyncSelectInput";
import { ActionSpinner } from "../../components/ActionSpinner";
import { FileUploadInput } from "../../components/forms/inputs/FileUploadInput";

const userProviderNameOptions = [
  { label: "member_cards", value: "rekom" },
  { label: "default", value: "default" },
];

const defaultGatewayOptions = [
  { label: "stripe", value: "stripe" },
  { label: "transpayrent", value: "transpayrent" },
];

const defaultRewardsOptions = [
  { label: "Disabled", value: "disabled" },
  { label: "Enabled", value: "enabled" },
  { label: "Enabled w. role", value: "enabled_role_required" },
];

const cardBodyStyle = css`
  gap: 16px;
  padding-bottom: 24px;
`;

const OrganizationUpsert: React.FC = () => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const { organizationToEdit } = useParams<{
    organizationToEdit: string;
  }>();

  const {
    data: organization,
    isLoading: isLoadingOrganization,
  } = useGetOrganization(organizationToEdit);

  const validationSchema = yup.object().shape({
    name: yup.string().required("Enter organization name to continue"),
    keyName: yup.string().required("Enter organization key name to continue"),
    supportEmail: yup
      .string()
      .email("Enter valid email to continue")
      .required("Enter support email to continue"),
  });

  const formMethods = useForm<Organization & { files: File[] }>({
    defaultValues: {
      userProviderCodeId: "default",
      defaultGateway: "stripe",
      keyName: "",
      supportEmail: "",
      files: [],
      guestEnabled: true,
    },
    resolver: yupResolver(validationSchema),
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = formMethods;
  const navigate = useNavigate();
  const {
    data: terms,
    isLoading: isLoadingTerms,
    error: getOrganizationTermsError,
  } = useGetOrganizationTerms(organizationToEdit);

  const { mutateAsync: updateOrganization } = useUpdateOrganization(
    organizationToEdit
  );
  const {
    mutateAsync: uploadOrganizationReceiptImage,
  } = useUploadOrganizationReceiptImage(organizationToEdit);

  const termsOptions = (terms || []).map((t) => ({
    value: t.keyName,
    label: t.title,
  }));

  termsOptions.unshift({
    label: "None",
    value: "",
  });

  const logoUrl = formMethods.watch("logoUrl");

  useHandleFetchError(getOrganizationTermsError);

  React.useEffect(() => {
    if (organization) {
      reset({
        ...organization,
        files: [],
        defaultHeapsGoWebDomain: organization.defaultHeapsGoDomain || "",
      });
    }
  }, [organization, reset]);

  const onSubmit = async (data: Organization & { files: File[] }) => {
    const { files, ...rest } = data;
    if (rest.rewardsProgramStatus === "disabled") {
      rest.rewardsProgramRequiredTerm = "";
    }

    if (!rest.defaultHeapsGoWebDomain) {
      rest.defaultHeapsGoWebDomain = null;
    }
    try {
      if (files.length > 0) {
        const receiptImageUrl = await uploadOrganizationReceiptImage({
          file: files[0],
          organizationKeyName: organizationToEdit!,
        });
        rest.logoUrl = receiptImageUrl;
      }
      await updateOrganization({
        organizationKeyName: organizationToEdit!,
        data: rest,
      });
      toastr.success(
        "Success",
        "Organization saved successfully. Changes will be visible in about 10 minutes."
      );
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
    navigate(routes.organizationsList(organizationKeyName));
  };

  const retrievePromotionCodesOptions = async (inputValue: string) => {
    try {
      const promotionCodes = await http.getPromotionCodes(organizationToEdit!, {
        filterByCode: inputValue,
      });

      const codes = promotionCodes
        .filter((p) => p.scope === "scope.redeem")
        .map((pc) => ({
          value: pc.code,
          label: pc.code,
        }));
      codes.unshift({
        label: "None",
        value: "",
      });
      return codes;
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
      return [];
    }
  };

  if (isLoadingOrganization || isLoadingTerms) {
    return <SpinnerBlock />;
  }

  return (
    <>
      <FormProvider {...formMethods}>
        <PageWrapper>
          <GoBackLinkButton
            to={routes.organizationsList(organizationKeyName)}
            text="Back to all organizations"
          />
          <StyledForm>
            <CardContainer>
              <PageHeader
                title="Organization Edit"
                subtitle="Edit the details of this organization"
              />
              <CardBody />
            </CardContainer>
            <CardContainer>
              <PageHeader
                title="General information"
                subtitle="Enter name of organization and logo url"
              />
              <CardBody additionalStyles={cardBodyStyle}>
                <Inputs.FormRow>
                  <TextInput label="Name" name="name" />
                </Inputs.FormRow>
                <Inputs.FormRow>
                  <TextInput
                    label="HeapsGo Web Domain"
                    name="defaultHeapsGoWebDomain"
                    helperText="Leave out if you want to use the default domain."
                  />
                </Inputs.FormRow>
              </CardBody>
            </CardContainer>
            <CardContainer>
              <PageHeader
                title="Receipt logo"
                subtitle="Upload logo for order receipts. OBS: Must be black."
              />
              <CardBody additionalStyles={cardBodyStyle}>
                <FileUploadInput multiple={false} />
                {logoUrl && (
                  <>
                    <StyledText
                      className="bodySubtextSemibold mb-0"
                      color={colors.textPrimary}
                    >
                      Currently used logo
                    </StyledText>
                    <BaseUploadPreview url={logoUrl} />
                  </>
                )}
              </CardBody>
            </CardContainer>
            <CardContainer>
              <PageHeader
                title="Referral"
                subtitle="Configure the referral program. Choose the code and the reward"
              />
              <CardBody additionalStyles={cardBodyStyle}>
                <AsyncSelectInput
                  label="Referral Bonus Code"
                  name="referralBonusCode"
                  defaultOptions
                  retrieveAsyncOptions={retrievePromotionCodesOptions}
                />
                <AsyncSelectInput
                  label="User referral code"
                  name="userReferralCode"
                  defaultOptions
                  retrieveAsyncOptions={retrievePromotionCodesOptions}
                />
              </CardBody>
            </CardContainer>
            <CardContainer>
              <PageHeader
                title="Payment"
                subtitle="Enter AWS Pinpoint application id and the default payment gateway"
              />
              <CardBody additionalStyles={cardBodyStyle}>
                <Inputs.FormRow>
                  <TextInput
                    name="awsPinPointApplicationId"
                    label="AWS Pinpoint Application Id"
                  />
                  <SelectInput
                    label="Default Gateway"
                    name="defaultGateway"
                    options={defaultGatewayOptions}
                  />
                </Inputs.FormRow>
                <Inputs.FormRow>
                  <TextInput label="Stripe Account Id" name="stripeAccountId" />
                </Inputs.FormRow>
                <Inputs.FormRow>
                  <TextInput
                    label="Unique Credit Cards Count Limit"
                    name="uniqueCreditCardsCountLimit"
                  />
                </Inputs.FormRow>
              </CardBody>
            </CardContainer>
            <CardContainer>
              <PageHeader
                title="OnlinePOS"
                subtitle="Configure OnlinePOS configuration"
              />
              <CardBody additionalStyles={cardBodyStyle}>
                <Inputs.FormRow>
                  <TextInput
                    label="Online POS Beacon Shared Secret"
                    name="onlinePosBeaconSharedSecret"
                  />
                  <TextInput
                    label="Online POS Firma Id"
                    name="onlinePosFirmaId"
                  />
                </Inputs.FormRow>
                <Inputs.FormRow>
                  <TextInput
                    label="Online POS API Token"
                    name="onlinePosApiToken"
                  />
                  <TextInput
                    label="Online POS Koncern Id"
                    name="onlinePosKoncernId"
                  />
                </Inputs.FormRow>
              </CardBody>
            </CardContainer>
            <CardContainer>
              <PageHeader
                title="Emails"
                subtitle="Configure email sending and support email"
              />
              <CardBody additionalStyles={cardBodyStyle}>
                <CheckboxInput
                  label="Send Referral Mail. If disabled, referral email won't be sent"
                  name="sendReferralMail"
                />
                <CheckboxInput
                  label="Send Welcome Mail. If disabled, welcome email won't be sent"
                  name="sendWelcomeMail"
                />
                <Inputs.FormRow>
                  <TextInput label="Support Email" name="supportEmail" />
                </Inputs.FormRow>
              </CardBody>
            </CardContainer>
            <CardContainer>
              <PageHeader
                title="Marketing"
                subtitle="Configure your challenges program and analytics tool Mixpanel"
              />
              <CardBody additionalStyles={cardBodyStyle}>
                <Inputs.FormRow>
                  <TextInput label="Mixpanel Token" name="mixpanelToken" />
                </Inputs.FormRow>
                <Inputs.FormRow>
                  <SelectInput
                    label="Enable challenge program"
                    name="rewardsProgramStatus"
                    helperText="If 'disabled', the challenge program is not active and no user can start challenges"
                    options={defaultRewardsOptions}
                  />
                </Inputs.FormRow>
                <Inputs.FormRow>
                  <SelectInput
                    options={termsOptions}
                    label="Required terms (optional)"
                    name="rewardsProgramRequiredTerm"
                    helperText="Choose if the user should accept any terms to participate in the challenge program"
                  />
                </Inputs.FormRow>
              </CardBody>
            </CardContainer>

            <CardContainer>
              <PageHeader title="Advanced configuration" />
              <CardBody additionalStyles={cardBodyStyle}>
                <Inputs.FormRow>
                  <SelectInput
                    options={userProviderNameOptions}
                    label="User Provider Name"
                    name="userProviderCodeId"
                    menuPlacement="top"
                  />
                </Inputs.FormRow>
              </CardBody>
            </CardContainer>
          </StyledForm>
        </PageWrapper>
        <StickyBottomBar>
          <div css={stickyBottomBarBodyStyle}>
            <ButtonAction
              onClick={handleSubmit(onSubmit)}
              type="button"
              disabled={isSubmitting}
            >
              {isSubmitting ? <ActionSpinner /> : "Save"}
            </ButtonAction>
          </div>
        </StickyBottomBar>
      </FormProvider>
    </>
  );
};

export default OrganizationUpsert;
